import React from "react";
import { useTranslation } from "react-i18next";

/**
 * Welcome banner + watchlist count + "updating" indicator. Extracted
 * verbatim from UnifiedStockerDashboard.jsx's hero block -- pure
 * presentational, no data-fetching, no derived-state computation.
 */
const DashboardHero = ({ user, expandedStock, watchlistCount, dataLoading }) => {
  const { t } = useTranslation();

  return (
    // The gradient tint below was applied unconditionally -- fine on the
    // dark surface it was designed against, but with no light counterpart
    // it read as a washed, dingy lavender card sitting on an otherwise
    // clean white/neutral dashboard (confirmed live via a user screenshot,
    // 2026-09-11). Light mode now gets a real white card with a violet
    // accent border instead of a tinted fill; the gradient is dark-mode-only.
    <div className="mb-4 sm:mb-6 p-3 sm:p-4 bg-white dark:bg-gradient-to-r dark:from-blue-500/10 dark:via-purple-500/10 dark:to-blue-500/10 border border-violet-200 dark:border-blue-500/20 rounded-xl">
      <div className="flex flex-col sm:flex-row items-start justify-between gap-3 sm:gap-4">
        <div className="flex-1">
          <h2 className="text-xs sm:text-sm font-bold text-gray-800 dark:text-slate-200 mb-1">
            {t("dashboard.unifiedDashboard.welcomeBack", {
              name: user?.full_name || user?.username || t("dashboard.unifiedDashboard.trader"),
            })}
          </h2>
          <p className="text-[10px] sm:text-xs text-gray-500 dark:text-slate-400 leading-relaxed">
            {t("dashboard.unifiedDashboard.heroSubtitle")}
          </p>
          {expandedStock && (
            <p className="text-[10px] sm:text-[11px] text-blue-300 mt-2">
              {t("dashboard.unifiedDashboard.currentlyViewing")}{" "}
              <span className="font-semibold">{expandedStock}</span>{" "}
              {t("dashboard.unifiedDashboard.clickToCollapse")}
            </p>
          )}
        </div>

        <div className="flex flex-row sm:flex-col items-center sm:items-end gap-2 sm:gap-1 w-full sm:w-auto">
          <div className="flex items-center gap-2">
            <span className="text-[10px] sm:text-xs text-gray-500 dark:text-slate-500">
              {t("dashboard.unifiedDashboard.watchlistLabel")}
            </span>
            <span className="text-[10px] sm:text-xs font-medium text-gray-600 dark:text-slate-300">
              {t("dashboard.unifiedDashboard.stocksCount", { count: watchlistCount })}
            </span>
          </div>
          {dataLoading && (
            <div className="flex items-center gap-2">
              <div className="w-2 h-2 bg-blue-500 rounded-full animate-pulse" />
              <span className="text-[10px] sm:text-xs text-gray-500 dark:text-slate-500">
                {t("dashboard.unifiedDashboard.updating")}
              </span>
            </div>
          )}
        </div>
      </div>
    </div>
  );
};

export default DashboardHero;
